import {
  AREA_PRESETS,
  buildingFootprint,
  crossAislesFor,
  inferRackGeometry,
  rackFootprint,
  type AreaPreset,
  type Coordinates3D,
  type Footprint,
  type LocationType,
  type MapBin,
  type RackGeometry,
  type RackKind,
  type WarehouseMap,
} from '@wms/domain';

// The warehouse in 2.5D: an orthographic camera turned around the vertical axis (yaw) and
// tilted down (pitch), drawn in SVG. Light enough for handheld terminals, no WebGL needed.
// Racks, pallet places and areas are drawn at their real size (warehouse_racks).

/** Drawing units per metre. */
export const UNIT = 20;
/** Levels are drawn taller than they are, so the shelves of a rack can be told apart. */
export const Z_SCALE = 2.2;
/** The classic isometric elevation, atan(1/√2). */
export const ISO_PITCH = 35.264;
export const MIN_PITCH = 20;
/** From this elevation on the map is a floor plan: one tile per rack position. */
export const PLAN_PITCH = 84;

export interface P2 {
  readonly x: number;
  readonly y: number;
}

/** Degrees: yaw turns the warehouse around, pitch 90 looks straight down. */
export interface View {
  readonly yaw: number;
  readonly pitch: number;
}

/** Dock and packing tables at the bottom, rows A, B, C … from left to right; its plan has x to the right and y up. */
export const DEFAULT_VIEW: View = { yaw: 225, pitch: ISO_PITCH };

/** One shelf: a rack level of one rack position (its bins share it), a floor area, or a building. */
export interface Cell {
  readonly key: string;
  /** the rack position (all levels) the shelf belongs to: "A|3" */
  readonly column: string;
  readonly label: string;
  readonly type: LocationType | 'building';
  readonly kind: RackKind | 'building';
  /** a building's id (the office): drawn like a rack, holds no bins */
  readonly building?: string;
  readonly blocked: boolean;
  /** location codes of the bins on the shelf */
  readonly codes: readonly string[];
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  readonly z0: number;
  readonly z1: number;
}

/** Text on the floor: a row letter in front of the row, a rack number on its rack. */
export interface FloorLabel {
  readonly key: string;
  readonly text: string;
  readonly at: Coordinates3D;
  /** metres the label may take up; it is left out when that is too small on screen */
  readonly room: number;
}

export interface World {
  readonly cells: readonly Cell[];
  /** one tile per rack position, for the floor plan */
  readonly columns: readonly Cell[];
  /** row letters, in front of the first rack of each row */
  readonly aisleLabels: readonly { readonly aisle: string; readonly at: Coordinates3D }[];
  /** rack numbers 1, 2, 3 … on top of each rack */
  readonly rackLabels: readonly FloorLabel[];
  /** every rack and area drawn, by "A|3" */
  readonly racks: ReadonlyMap<string, RackGeometry>;
  readonly center: P2;
  readonly cross: readonly number[];
  /** smallest level step, metres */
  readonly zStep: number;
  readonly alongIsY: boolean;
  /** corners of every shelf: what "the whole warehouse" frames */
  readonly corners: readonly Coordinates3D[];
  /** the floor the racks and areas stand on */
  readonly bounds: Footprint | null;
}

export const AREA_LABELS: Partial<Record<LocationType, string>> = {
  receiving: 'Kabul',
  packing: 'Paketleme',
  damaged: 'Hasarlı',
  quarantine: 'Karantina',
  returns: 'İade',
  staging: 'Hazırlık',
  shipping: 'Sevk',
};
export const rackKeyOf = (aisle: string, rack: number) => `${aisle}|${rack}`;
export const cellKey = (aisle: string, rack: number, level: number) => `${aisle}|${rack}|${level}`;
const isStorage = (type: LocationType) => type === 'pick' || type === 'bulk';
const pad2 = (n: number) => String(n).padStart(2, '0');

/** How tall one level of a rack is drawn (a little lower than the level, so levels show apart). */
function levelBox(g: RackGeometry, level: number): { z0: number; z1: number } {
  const step = g.levelHeight > 0.05 ? g.levelHeight : 0.4;
  const z0 = Math.max(0, level - 1) * step;
  return { z0, z1: z0 + step * (g.kind === 'floor' || g.kind === 'ibc' ? 0.94 : 0.82) };
}

/**
 * Racks, shelves and areas in metres. Every rack is drawn at its stored size; racks built
 * before sizes were stored get the size inferred from their bins (as the server does).
 */
export function buildWorld(map: WarehouseMap): World {
  const alongIsY = (map.layout.aisleAxis ?? 'y') === 'y';
  const racks = new Map((map.racks ?? []).map((r) => [rackKeyOf(r.aisle, r.rack), r]));
  const missing = map.bins.filter((b) => !racks.has(rackKeyOf(b.aisle, b.rack)));
  if (missing.length > 0) {
    const aisles = new Set(missing.map((b) => b.aisle));
    for (const r of inferRackGeometry(
      map.bins.filter((b) => aisles.has(b.aisle)),
      map.layout.aisleAxis ?? 'y',
    )) {
      const key = rackKeyOf(r.aisle, r.rack);
      if (!racks.has(key)) racks.set(key, r);
    }
  }

  const groups = new Map<string, MapBin[]>();
  for (const b of map.bins) {
    const key = isStorage(b.type) ? cellKey(b.aisle, b.rack, b.level) : `${rackKeyOf(b.aisle, b.rack)}|area`;
    groups.set(key, [...(groups.get(key) ?? []), b]);
  }
  const areasPerAisle = new Map<string, Set<number>>();
  for (const b of map.bins) if (!isStorage(b.type)) areasPerAisle.set(b.aisle, (areasPerAisle.get(b.aisle) ?? new Set()).add(b.rack));

  const cells: Cell[] = [];
  let zStep = Infinity;
  for (const [key, group] of groups) {
    const first = group[0]!;
    const g = racks.get(rackKeyOf(first.aisle, first.rack));
    if (!g) continue;
    const f = rackFootprint(g);
    const column = rackKeyOf(first.aisle, first.rack);
    if (!isStorage(first.type)) {
      const many = (areasPerAisle.get(first.aisle)?.size ?? 0) > 1;
      // an area built with a row letter on the old screen shows it: that letter is taken
      const ownCode = (AREA_PRESETS as Partial<Record<LocationType, AreaPreset>>)[first.type]?.code === first.aisle;
      cells.push({
        key,
        column,
        label:
          g.name ??
          (ownCode
            ? `${AREA_LABELS[first.type] ?? first.aisle}${many ? ` ${first.rack}` : ''}`
            : `${AREA_LABELS[first.type] ?? 'Alan'} ${first.aisle}${many ? `-${pad2(first.rack)}` : ''}`),
        type: first.type,
        kind: 'area',
        blocked: group.some((b) => b.isBlocked),
        codes: group.map((b) => b.code),
        ...f,
        z0: 0,
        z1: 0.05,
      });
      continue;
    }
    const { z0, z1 } = levelBox(g, first.level);
    if (g.levelHeight > 0.05) zStep = Math.min(zStep, g.levelHeight);
    cells.push({
      key,
      column,
      label: `${first.aisle}-${pad2(first.rack)}-${first.level}`,
      type: first.type,
      kind: g.kind,
      blocked: group.some((b) => b.isBlocked),
      codes: group.map((b) => b.code),
      ...f,
      z0,
      z1,
    });
  }

  const byColumn = new Map<string, Cell[]>();
  for (const c of cells) byColumn.set(c.column, [...(byColumn.get(c.column) ?? []), c]);
  const columns = [...byColumn.entries()].map(([column, stack]): Cell => {
    const first = stack[0]!;
    if (stack.length === 1 && first.kind === 'area') return first;
    return {
      ...first,
      key: column,
      label: first.label.replace(/-\d+$/, ''),
      blocked: stack.some((c) => c.blocked),
      codes: stack.flatMap((c) => c.codes),
      z0: Math.min(...stack.map((c) => c.z0)),
      z1: Math.max(...stack.map((c) => c.z1)),
    };
  });

  // buildings on the floor (the office): boxes at their height, their name on the roof
  for (const b of map.layout.buildings ?? []) {
    const f = buildingFootprint(b);
    const cell: Cell = { key: `building:${b.id}`, column: `building:${b.id}`, label: b.name, type: 'building', kind: 'building', building: b.id, blocked: false, codes: [], ...f, z0: 0, z1: b.height };
    cells.push(cell);
    columns.push(cell);
  }

  // a row letter in front of the row's first rack, a number on every rack
  const aisleLabels: { aisle: string; at: Coordinates3D }[] = [];
  const rackLabels: FloorLabel[] = [];
  const rows = new Map<string, RackGeometry[]>();
  for (const column of columns) {
    const g = racks.get(column.column);
    if (!g || g.kind === 'area') continue;
    rows.set(g.aisle, [...(rows.get(g.aisle) ?? []), g]);
    rackLabels.push({ key: column.column, text: String(g.rack), at: { x: g.x, y: g.y, z: column.z1 }, room: Math.min(g.width, g.depth * 1.6) });
  }
  for (const [aisle, row] of rows) {
    const sorted = [...row].sort((a, b) => a.rack - b.rack);
    const first = sorted[0]!;
    const last = sorted.at(-1)!;
    const len = Math.hypot(last.x - first.x, last.y - first.y);
    const dir = len > 1e-6 ? { x: (last.x - first.x) / len, y: (last.y - first.y) / len } : alongIsY ? { x: 0, y: 1 } : { x: 1, y: 0 };
    const out = first.width / 2 + 0.7;
    aisleLabels.push({ aisle, at: { x: first.x - dir.x * out, y: first.y - dir.y * out, z: 0 } });
  }

  const corners = cells.flatMap((c) => [c.z0, c.z1].flatMap((z) => [c.x0, c.x1].flatMap((x) => [c.y0, c.y1].map((y) => ({ x, y, z })))));
  const xs = cells.flatMap((c) => [c.x0, c.x1]);
  const ys = cells.flatMap((c) => [c.y0, c.y1]);
  const bounds = xs.length ? { x0: Math.min(...xs), x1: Math.max(...xs), y0: Math.min(...ys), y1: Math.max(...ys) } : null;
  const center = bounds ? { x: (bounds.x0 + bounds.x1) / 2, y: (bounds.y0 + bounds.y1) / 2 } : { x: 0, y: 0 };
  return {
    cells,
    columns,
    aisleLabels,
    rackLabels,
    racks,
    center,
    cross: crossAislesFor(map.layout, map.bins.map((b) => ({ coordinates: b.at }))),
    zStep: Number.isFinite(zStep) ? zStep : 0.4,
    alongIsY,
    corners,
    bounds,
  };
}

const NORMAL: Readonly<Record<number, P2>> = { 0: { x: 1, y: 0 }, 90: { x: 0, y: 1 }, 180: { x: -1, y: 0 }, 270: { x: 0, y: -1 } };

/** Where a picker stands to reach a bin is its coordinate; the marker sits on the open side of its rack. */
export function shelfFace(world: World, code: string, at: Coordinates3D): Coordinates3D {
  const [aisle, rack] = code.split('-');
  const g = aisle && rack ? world.racks.get(rackKeyOf(aisle, Number(rack))) : undefined;
  if (!g || g.kind === 'area') return { ...at, z: at.z + world.zStep * 0.4 };
  const n = NORMAL[g.facing] ?? { x: 0, y: 0 };
  const z = at.z + (g.levelHeight > 0.05 ? g.levelHeight : world.zStep) * 0.4;
  return { x: at.x - n.x * g.reach, y: at.y - n.y * g.reach, z };
}

/** Metre lines on the floor around the racks: thin every metre, strong every five. */
export interface FloorGrid {
  readonly lines: readonly { readonly from: Coordinates3D; readonly to: Coordinates3D; readonly major: boolean }[];
  readonly ticks: readonly { readonly text: string; readonly at: Coordinates3D }[];
}

export function floorGrid(bounds: Footprint | null): FloorGrid {
  if (!bounds) return { lines: [], ticks: [] };
  const x0 = Math.floor(bounds.x0 - 1);
  const x1 = Math.ceil(bounds.x1 + 1);
  const y0 = Math.floor(bounds.y0 - 1);
  const y1 = Math.ceil(bounds.y1 + 1);
  // a big floor gets lines every 2 or 5 metres instead
  const span = Math.max(x1 - x0, y1 - y0);
  const step = span > 160 ? 5 : span > 80 ? 2 : 1;
  const lines: FloorGrid['lines'][number][] = [];
  const ticks: FloorGrid['ticks'][number][] = [];
  for (let x = Math.ceil(x0 / step) * step; x <= x1; x += step) {
    lines.push({ from: { x, y: y0, z: 0 }, to: { x, y: y1, z: 0 }, major: x % 5 === 0 });
    if (x % 5 === 0) ticks.push({ text: `${x}`, at: { x, y: y0 - 0.6, z: 0 } });
  }
  for (let y = Math.ceil(y0 / step) * step; y <= y1; y += step) {
    lines.push({ from: { x: x0, y, z: 0 }, to: { x: x1, y, z: 0 }, major: y % 5 === 0 });
    if (y % 5 === 0) ticks.push({ text: `${y}`, at: { x: x0 - 0.6, y, z: 0 } });
  }
  ticks.push({ text: 'X (m)', at: { x: x1 + 1.4, y: y0 - 0.6, z: 0 } }, { text: 'Y (m)', at: { x: x0 - 0.6, y: y1 + 1.2, z: 0 } });
  return { lines, ticks };
}

/** A scale bar length (metres) that is 60–160 pixels long at this many pixels per metre. */
export function scaleBar(pixelsPerMetre: number): { readonly metres: number; readonly pixels: number } {
  const options = [0.5, 1, 2, 5, 10, 20, 50, 100, 200];
  const metres = options.find((m) => m * pixelsPerMetre >= 60) ?? options.at(-1)!;
  return { metres, pixels: metres * pixelsPerMetre };
}

export const clampPitch = (pitch: number) => Math.min(90, Math.max(MIN_PITCH, pitch));

export interface Projector {
  readonly view: View;
  /** World point (metres) to drawing units, relative to the world's centre. */
  readonly project: (p: Coordinates3D) => P2;
  /** Horizontal distance toward the viewer (metres): nearer shelves are drawn later. */
  readonly depth: (x: number, y: number) => number;
  /** The level-plane world offset (metres) that moves a point by (dx, dy) drawing units. */
  readonly unproject: (dx: number, dy: number) => { readonly x: number; readonly y: number };
  /** A horizontal direction (a face normal) on screen: side < 0 looks left, toward > 0 faces the viewer. */
  readonly turn: (nx: number, ny: number) => { readonly side: number; readonly toward: number };
}

export function projector(view: View, origin: P2): Projector {
  const t = (view.yaw * Math.PI) / 180;
  const p = (clampPitch(view.pitch) * Math.PI) / 180;
  const c = Math.cos(t);
  const s = Math.sin(t);
  const sp = Math.sin(p);
  const cp = Math.cos(p);
  // the camera stands on the far side of the floor point it looks at, so its right hand points
  // along −(x·cos − y·sin): drawn the other way round the floor would be a mirror image
  return {
    view,
    project: ({ x, y, z }) => {
      const dx = x - origin.x;
      const dy = y - origin.y;
      return { x: -(dx * c - dy * s) * UNIT, y: ((dx * s + dy * c) * sp - z * Z_SCALE * cp) * UNIT };
    },
    depth: (x, y) => (x - origin.x) * s + (y - origin.y) * c,
    unproject: (dx, dy) => {
      const u = -dx / UNIT;
      const v = dy / (UNIT * sp);
      return { x: u * c + v * s, y: -u * s + v * c };
    },
    turn: (nx, ny) => ({ side: -(nx * c - ny * s), toward: (nx * s + ny * c) * cp }),
  };
}

/** A visible side of a shelf: its outline and how much it is turned away from the light (0–1). */
export interface Face {
  readonly points: string;
  readonly shade: number;
}

export interface Box {
  readonly cell: Cell;
  readonly sides: readonly Face[];
  readonly top: string;
  readonly topCenter: P2;
  readonly depth: number;
}

export const toPoints = (ps: readonly P2[]) => ps.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

// sides by outward normal, each from corner a to corner b (indices into [x0, x1] and [y0, y1])
const SIDES = [
  { nx: 0, ny: -1, a: [0, 0], b: [1, 0] },
  { nx: 1, ny: 0, a: [1, 0], b: [1, 1] },
  { nx: 0, ny: 1, a: [1, 1], b: [0, 1] },
  { nx: -1, ny: 0, a: [0, 1], b: [0, 0] },
] as const;
// in the classic view one side looks 45° left (lit) and one 45° right (shaded)
const LIT = -Math.SQRT1_2;
const DARK = Math.SQRT1_2;

/**
 * The shelves as polygons, far ones first (painter's algorithm). Shelves stand in rows with
 * aisles between them, so ordering by the horizontal depth of the footprint and then by
 * height paints every overlap the right way round.
 */
export function boxes(cells: readonly Cell[], proj: Projector): Box[] {
  return cells
    .map((cell): Box => {
      const X = [cell.x0, cell.x1] as const;
      const Y = [cell.y0, cell.y1] as const;
      const P = (i: number, j: number, z: number) => proj.project({ x: X[i as 0 | 1], y: Y[j as 0 | 1], z });
      const top = [P(0, 0, cell.z1), P(1, 0, cell.z1), P(1, 1, cell.z1), P(0, 1, cell.z1)];
      const sides: Face[] = [];
      for (const f of SIDES) {
        const { side, toward } = proj.turn(f.nx, f.ny);
        if (toward <= 1e-3) continue;
        sides.push({
          points: toPoints([P(f.a[0], f.a[1], cell.z0), P(f.b[0], f.b[1], cell.z0), P(f.b[0], f.b[1], cell.z1), P(f.a[0], f.a[1], cell.z1)]),
          shade: Math.min(1, Math.max(0, (side - LIT) / (DARK - LIT))),
        });
      }
      return {
        cell,
        sides,
        top: toPoints(top),
        topCenter: { x: top.reduce((s, p) => s + p.x, 0) / 4, y: top.reduce((s, p) => s + p.y, 0) / 4 },
        depth: proj.depth((cell.x0 + cell.x1) / 2, (cell.y0 + cell.y1) / 2),
      };
    })
    .sort((a, b) => a.depth - b.depth || a.cell.z0 - b.cell.z0);
}

// --- camera -----------------------------------------------------------------------------

export interface Camera extends View {
  /** screen pixels per drawing unit */
  readonly scale: number;
  /** world point (metres) in the middle of the map */
  readonly target: Coordinates3D;
}

export interface Size {
  readonly w: number;
  readonly h: number;
}

export interface ScaleLimits {
  readonly min: number;
  readonly max: number;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Pixels from the map's top-left corner where a world point lands. */
export function screenOf(cam: Camera, proj: Projector, size: Size): (p: Coordinates3D) => P2 {
  const t = proj.project(cam.target);
  return (p) => {
    const q = proj.project(p);
    return { x: size.w / 2 + (q.x - t.x) * cam.scale, y: size.h / 2 + (q.y - t.y) * cam.scale };
  };
}

const moved = (cam: Camera, d: { x: number; y: number }): Camera => ({ ...cam, target: { x: cam.target.x + d.x, y: cam.target.y + d.y, z: cam.target.z } });

/** The camera after the content was dragged by (dx, dy) pixels. */
export function panned(cam: Camera, origin: P2, dx: number, dy: number): Camera {
  return moved(cam, projector(cam, origin).unproject(-dx / cam.scale, -dy / cam.scale));
}

/** Zooms by `factor`, keeping the world point under `at` (pixels) where it is. */
export function zoomed(cam: Camera, origin: P2, size: Size, at: P2, factor: number, limits: ScaleLimits): Camera {
  const scale = clamp(cam.scale * factor, limits.min, limits.max);
  const f = 1 / cam.scale - 1 / scale;
  return { ...moved(cam, projector(cam, origin).unproject((at.x - size.w / 2) * f, (at.y - size.h / 2) * f)), scale };
}

/** Turns the warehouse by a drag: sideways turns it around (the near side follows the pointer), up and down tilts it. */
export function orbited(cam: Camera, dx: number, dy: number): Camera {
  return { ...cam, yaw: cam.yaw + dx * 0.4, pitch: clampPitch(cam.pitch + dy * 0.35) };
}

/** Turns the warehouse by `turn` degrees around the point under `at` (two-finger twist). */
export function turned(cam: Camera, origin: P2, size: Size, at: P2, turn: number): Camera {
  const ox = (at.x - size.w / 2) / cam.scale;
  const oy = (at.y - size.h / 2) / cam.scale;
  const next = { ...cam, yaw: cam.yaw + turn };
  const before = projector(cam, origin).unproject(ox, oy);
  const after = projector(next, origin).unproject(ox, oy);
  return moved(next, { x: before.x - after.x, y: before.y - after.y });
}

/** Room the toolbar and the edges take from the picture, pixels. */
export interface Margins {
  readonly top: number;
  readonly side: number;
  readonly bottom: number;
}

/** The camera that shows all the points from the given angle. */
export function framed(points: readonly Coordinates3D[], view: View, origin: P2, size: Size, margins: Margins, limits: ScaleLimits): Camera {
  const proj = projector(view, origin);
  const ps = points.map(proj.project);
  if (ps.length === 0) return { ...view, scale: limits.min, target: { x: origin.x, y: origin.y, z: 0 } };
  const minX = Math.min(...ps.map((p) => p.x));
  const maxX = Math.max(...ps.map((p) => p.x));
  const minY = Math.min(...ps.map((p) => p.y));
  const maxY = Math.max(...ps.map((p) => p.y));
  const roomW = Math.max(size.w - 2 * margins.side, 40);
  const roomH = Math.max(size.h - margins.top - margins.bottom, 40);
  const scale = clamp(Math.min(roomW / Math.max(maxX - minX, 1), roomH / Math.max(maxY - minY, 1)), limits.min, limits.max);
  // the box goes in the middle of the free room, below the toolbar
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2 - (margins.top - margins.bottom) / 2 / scale;
  const d = proj.unproject(cx, cy);
  return { ...view, scale, target: { x: origin.x + d.x, y: origin.y + d.y, z: 0 } };
}

/** Halfway cameras for a flight: the shorter way round, zoom evenly in log scale. */
export function blend(a: Camera, b: Camera, t: number): Camera {
  const turn = ((((b.yaw - a.yaw) % 360) + 540) % 360) - 180;
  return {
    yaw: a.yaw + turn * t,
    pitch: a.pitch + (b.pitch - a.pitch) * t,
    scale: a.scale * Math.pow(b.scale / a.scale, t),
    target: {
      x: a.target.x + (b.target.x - a.target.x) * t,
      y: a.target.y + (b.target.y - a.target.y) * t,
      z: a.target.z + (b.target.z - a.target.z) * t,
    },
  };
}

/**
 * Straight down, squared to the aisles; back to the isometric angle from there. Halfway
 * angles go down for the plan and up for the isometric view, so the default view and its
 * plan (dock at the bottom in both) toggle into each other.
 */
export function planView(view: View): View {
  return { yaw: Math.round((view.yaw - 1e-6) / 90) * 90, pitch: 90 };
}
export function isoView(view: View): View {
  return { yaw: Math.round((view.yaw - 45) / 90) * 90 + 45, pitch: ISO_PITCH };
}

// --- markers that would cover each other ------------------------------------------------

export interface Pin<T> {
  readonly id: string;
  readonly at: P2;
  readonly item: T;
}

export interface PinGroup<T> {
  readonly id: string;
  /** the middle of the members' own positions */
  readonly anchor: P2;
  readonly pins: readonly Pin<T>[];
  /** where each pin's badge is drawn, in order */
  readonly slots: readonly P2[];
  /** outline of the badges; above the anchor when there is more than one */
  readonly box: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
}

/**
 * Pins whose badges (diameter `size` px) would overlap go into one group, drawn side by side
 * in a bubble over their spot: two products from the same shelf stay readable as "2 3".
 */
export function groupPins<T>(pins: readonly Pin<T>[], size: number, gap = 3, perRow = 4): PinGroup<T>[] {
  const order = new Map(pins.map((p, i) => [p, i]));
  const pad = 3;
  const layout = (members: readonly Pin<T>[]): PinGroup<T> => {
    const sorted = [...members].sort((a, b) => order.get(a)! - order.get(b)!);
    const anchor = { x: sorted.reduce((s, p) => s + p.at.x, 0) / sorted.length, y: sorted.reduce((s, p) => s + p.at.y, 0) / sorted.length };
    const id = sorted.map((p) => p.id).join(',');
    if (sorted.length === 1) {
      return { id, anchor, pins: sorted, slots: [anchor], box: { x: anchor.x - size / 2, y: anchor.y - size / 2, w: size, h: size } };
    }
    const cols = Math.min(sorted.length, perRow);
    const rows = Math.ceil(sorted.length / cols);
    const step = size + gap;
    const w = cols * size + (cols - 1) * gap + 2 * pad;
    const h = rows * size + (rows - 1) * gap + 2 * pad;
    const box = { x: anchor.x - w / 2, y: anchor.y - size * 0.55 - h, w, h };
    const slots = sorted.map((_, i) => {
      const row = Math.floor(i / cols);
      const inRow = Math.min(cols, sorted.length - row * cols);
      const col = i - row * cols;
      return { x: anchor.x + (col - (inRow - 1) / 2) * step, y: box.y + pad + size / 2 + row * step };
    });
    return { id, anchor, pins: sorted, slots, box };
  };
  const overlap = (a: PinGroup<T>['box'], b: PinGroup<T>['box']) =>
    a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;

  let groups = pins.map((p) => layout([p]));
  for (let merging = true; merging; ) {
    merging = false;
    search: for (let i = 0; i < groups.length; i++) {
      for (let j = i + 1; j < groups.length; j++) {
        if (!overlap(groups[i]!.box, groups[j]!.box)) continue;
        const merged = layout([...groups[i]!.pins, ...groups[j]!.pins]);
        groups = groups.filter((_, k) => k !== i && k !== j);
        groups.splice(i, 0, merged);
        merging = true;
        break search;
      }
    }
  }
  return groups;
}
